import * as THREE from 'three';

import { PORTRAIT_POINT_COUNT, PORTRAIT_POSITIONS } from '../data/portraitPoints';

// Target: Mar 5, 2026 18:00 KST (UTC+9) = Mar 5, 2026 09:00 UTC
const COUNTDOWN_TARGET = new Date('2026-03-05T09:00:00Z');

const CANVAS_W = 1024;
const CANVAS_H = 320;
const SCALE = 0.4;
const RADIUS = 280;
const Y_OFFSET = 50;
const STEP = 2;
const MORPH_SPEED = 0.024;

// Scale factors for face points
const FACE_SCALE_X = 110;
const FACE_SCALE_Y = 100;
const FACE_SCALE_Z = 80;
const FACE_OFFSET_Y = 50;

const VERTEX_SHADER = `
  attribute float size;
  attribute vec3 color;
  attribute vec3 targetPosition;
  varying vec3 vColor;
  varying float vSize;
  uniform float time;
  uniform float morphProgress;

  void main() {
    vColor = color;
    vSize = size;

    // Interpolate between text and face positions
    vec3 pos = mix(position, targetPosition, morphProgress);

    // Subtle animation, less when morphed
    float animScale = 1.0 - morphProgress * 0.5;
    pos.x += sin(time * 1.5 + position.y * 0.05) * 0.2 * animScale;
    pos.y += cos(time * 1.0 + position.x * 0.05) * 0.2 * animScale;

    // Swirl effect during morph transition
    if (morphProgress > 0.0 && morphProgress < 1.0) {
      float swirl = sin(morphProgress * 3.14159) * 0.5;
      float angle = morphProgress * 6.28 + length(position.xy) * 0.02;
      pos.x += cos(angle) * swirl * 3.0;
      pos.y += sin(angle) * swirl * 3.0;
    }

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    float sizeMult = mix(0.65, 0.80, morphProgress);
    gl_PointSize = min(size * (600.0 / max(-mvPosition.z, 1.0)) * sizeMult, 256.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = `
  varying vec3 vColor;
  varying float vSize;
  uniform float time;
  uniform float morphProgress;

  void main() {
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;
    float alpha = 1.0 - smoothstep(0.0, 0.4, dist);
    float twinkle = 0.85 + 0.15 * sin(time * 4.0 + vSize * 10.0);

    // Brighter cyan glow when morphed to face
    vec3 faceColor = vec3(0.1, 0.8, 1.0);
    vec3 finalColor = mix(vColor, faceColor, morphProgress * 0.7);
    float brightMult = mix(0.65, 0.80, morphProgress);

    gl_FragColor = vec4(finalColor * twinkle * 2.0 * brightMult, alpha);
  }
`;

const getCountdownText = (): { line1: string; line2: string } => {
  const now = new Date();
  const diff = COUNTDOWN_TARGET.getTime() - now.getTime();
  if (diff <= 0) return { line1: '', line2: 'STARTING NOW!' };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  const pad = (n: number) => String(n).padStart(2, '0');

  return {
    line1: 'Starting in',
    line2: days + 'd ' + pad(hours) + ':' + pad(minutes) + ':' + pad(seconds),
  };
};

interface FaceData {
  points: number[];
  densities: number[];
  count: number;
}

const generateFacePoints = (): FaceData => {
  const points: number[] = [];
  const densities: number[] = [];

  for (let i = 0; i < PORTRAIT_POINT_COUNT; i++) {
    const x = PORTRAIT_POSITIONS[i * 3] * FACE_SCALE_X;
    const y = PORTRAIT_POSITIONS[i * 3 + 1] * FACE_SCALE_Y + FACE_OFFSET_Y;
    const z = PORTRAIT_POSITIONS[i * 3 + 2] * FACE_SCALE_Z;

    points.push(x, y, z);
    const density = 0.5 + PORTRAIT_POSITIONS[i * 3 + 2] * 3.0;
    densities.push(Math.min(1.0, density));
  }

  return { points, densities, count: points.length / 3 };
};

// Max stars that can appear in the countdown text (1024×320 canvas, step 2)
const MAX_STARS = 8192;

// Reusable typed arrays — allocated once, reused on every rebuild
const _posArr = new Float32Array(MAX_STARS * 3);
const _colArr = new Float32Array(MAX_STARS * 3);
const _sizeArr = new Float32Array(MAX_STARS);
const _targetArr = new Float32Array(MAX_STARS * 3);

// Offscreen canvas reused across rebuilds
const _canvas = document.createElement('canvas');
_canvas.width = CANVAS_W;
_canvas.height = CANVAS_H;
const _ctx = _canvas.getContext('2d', { willReadFrequently: true })!;

const fillStarBuffers = (faceData: FaceData): number => {
  _ctx.fillStyle = '#000';
  _ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  _ctx.fillStyle = '#fff';
  _ctx.textAlign = 'center';

  const cd = getCountdownText();
  _ctx.font = 'bold 44px Arial';
  _ctx.fillText(cd.line1, CANVAS_W / 2, CANVAS_H / 2 - 50);
  _ctx.font = 'bold 72px Arial';
  _ctx.fillText(cd.line2, CANVAS_W / 2, CANVAS_H / 2 + 40);

  const imageData = _ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
  const pixels = imageData.data;

  const faceCenter = { x: 0, y: Y_OFFSET, z: RADIUS };
  let count = 0;

  outer: for (let y = 0; y < CANVAS_H; y += STEP) {
    for (let x = 0; x < CANVAS_W; x += STEP) {
      if (pixels[(y * CANVAS_W + x) * 4] > 128) {
        const px = -(x - CANVAS_W / 2) * SCALE;
        const py = (CANVAS_H / 2 - y) * SCALE;
        const angle = px / RADIUS;
        const rJitter = (Math.random() - 0.5) * 8;

        const base = count * 3;
        _posArr[base] = RADIUS * Math.sin(angle) + (Math.random() - 0.5) * 1;
        _posArr[base + 1] = py + (Math.random() - 0.5) * 1 + Y_OFFSET;
        _posArr[base + 2] = RADIUS * Math.cos(angle) + rJitter;

        const brightness = 0.7 + Math.random() * 0.3;
        _colArr[base] = brightness * (0.05 + Math.random() * 0.15);
        _colArr[base + 1] = brightness * (0.6 + Math.random() * 0.3);
        _colArr[base + 2] = brightness * (0.9 + Math.random() * 0.1);

        _sizeArr[count] = 2.0 + Math.random() * 3.0;

        const faceIdx = count % faceData.count;
        _targetArr[base] = faceData.points[faceIdx * 3] + faceCenter.x;
        _targetArr[base + 1] = faceData.points[faceIdx * 3 + 1] + faceCenter.y;
        _targetArr[base + 2] = faceData.points[faceIdx * 3 + 2] + faceCenter.z;

        count++;
        if (count >= MAX_STARS) break outer;
      }
    }
  }

  return count;
};

/**
 * Birthday countdown stars + face morph.
 * Ported from: useBirthdayStars.ts
 */
export class BirthdaySystem {
  private group: THREE.Group;
  private material: THREE.ShaderMaterial;
  private points: THREE.Points | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private faceData: FaceData;

  private elapsed = 0;
  private morphProgress = 0;
  private morphDirection = 0; // 0 = none, 1 = to face, -1 = to text

  private rebuildTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(private scene: THREE.Scene) {
    this.group = new THREE.Group();
    scene.add(this.group);

    this.faceData = generateFacePoints();

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        morphProgress: { value: 0 },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });

    this.initGeometry();
    this.scheduleRebuild();
  }

  // Create geometry with pre-allocated max-size buffers (once)
  private initGeometry(): void {
    const count = fillStarBuffers(this.faceData);

    this.geometry = new THREE.BufferGeometry();
    // Use slices so Three.js knows the exact size
    this.geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(_posArr.slice(0, count * 3), 3)
    );
    this.geometry.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(_colArr.slice(0, count * 3), 3)
    );
    this.geometry.setAttribute(
      'size',
      new THREE.Float32BufferAttribute(_sizeArr.slice(0, count), 1)
    );
    this.geometry.setAttribute(
      'targetPosition',
      new THREE.Float32BufferAttribute(_targetArr.slice(0, count * 3), 3)
    );

    this.points = new THREE.Points(this.geometry, this.material);
    this.group.add(this.points);
  }

  // Update existing buffer attributes in-place — no GPU object recreation
  private rebuildGeometry(): void {
    if (!this.geometry || !this.points) return;

    const count = fillStarBuffers(this.faceData);

    const posAttr = this.geometry.attributes.position as THREE.BufferAttribute;
    const colAttr = this.geometry.attributes.color as THREE.BufferAttribute;
    const sizeAttr = this.geometry.attributes.size as THREE.BufferAttribute;
    const tgtAttr = this.geometry.attributes.targetPosition as THREE.BufferAttribute;

    // Resize if star count changed (rare — text length variation)
    if (posAttr.count !== count) {
      this.geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(_posArr.slice(0, count * 3), 3)
      );
      this.geometry.setAttribute(
        'color',
        new THREE.Float32BufferAttribute(_colArr.slice(0, count * 3), 3)
      );
      this.geometry.setAttribute(
        'size',
        new THREE.Float32BufferAttribute(_sizeArr.slice(0, count), 1)
      );
      this.geometry.setAttribute(
        'targetPosition',
        new THREE.Float32BufferAttribute(_targetArr.slice(0, count * 3), 3)
      );
    } else {
      // Same count — copy into existing arrays and mark dirty
      (posAttr.array as Float32Array).set(_posArr.subarray(0, count * 3));
      (colAttr.array as Float32Array).set(_colArr.subarray(0, count * 3));
      (sizeAttr.array as Float32Array).set(_sizeArr.subarray(0, count));
      (tgtAttr.array as Float32Array).set(_targetArr.subarray(0, count * 3));
      posAttr.needsUpdate = true;
      colAttr.needsUpdate = true;
      sizeAttr.needsUpdate = true;
      tgtAttr.needsUpdate = true;
    }

    this.material.uniforms.morphProgress.value = this.morphProgress;
  }

  // setTimeout-based loop: next rebuild fires 1s AFTER current one finishes
  private scheduleRebuild(): void {
    this.rebuildTimeout = setTimeout(() => {
      if (this.destroyed) return;
      this.rebuildGeometry();
      this.scheduleRebuild();
    }, 1000);
  }

  tick(dt: number): void {
    this.elapsed += dt;
    this.material.uniforms.time.value = this.elapsed;

    if (this.morphDirection !== 0) {
      this.morphProgress += this.morphDirection * MORPH_SPEED;

      if (this.morphProgress >= 1) {
        this.morphProgress = 1;
        this.morphDirection = 0;
      } else if (this.morphProgress <= 0) {
        this.morphProgress = 0;
        this.morphDirection = 0;
      }

      this.material.uniforms.morphProgress.value = this.morphProgress;
    }
  }

  startMorphToFace(): void {
    this.morphDirection = 1;
  }

  startMorphToText(): void {
    this.morphDirection = -1;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.rebuildTimeout !== null) {
      clearTimeout(this.rebuildTimeout);
      this.rebuildTimeout = null;
    }
    if (this.geometry) {
      this.geometry.dispose();
    }
    this.material.dispose();
    this.scene.remove(this.group);
  }
}
